/**
 * The output of {@link planImport}: table-ready records keyed by natural keys (no database
 * ids), the outcome of every source record, and the review queue.
 * @module
 */
import type { Cents } from '../money/index.js';
import type {
  ChannelVeto,
  DeductionMode,
  FamilyRole,
  IdentifierKind,
  PrecisionWarning,
  ProductRole,
  ServiceKind,
  SkuIssue,
  StateRuleState,
} from '../catalogue/index.js';
import type { ManualDecision } from './rules.js';

/** Format of {@link ImportPlan}. */
export const IMPORT_PLAN_FORMAT = 1;

/** A category (reporting only). Deleted source categories are planned archived. */
export interface PlannedCategory {
  key: string;
  sourceRef: string;
  name: string;
  archived: boolean;
}

/** A source record absorbed by a family without a product of its own. */
export interface AbsorbedRecord {
  sourceRef: string;
  as: 'recipe_twin' | 'state_member';
  /** For a state member: its state key. */
  state: string | null;
}

/** A product family: its member products plus the source items folded into it. */
export interface PlannedFamily {
  key: string;
  name: string;
  members: { productKey: string; familyRole: FamilyRole }[];
  absorbed: AbsorbedRecord[];
}

/** How the standard cost was found. */
export type CostMethod = 'source' | 'rollup' | 'manual';

/** Why a product has no standard cost. */
export type CostGap =
  | { code: 'no_cost' }
  | { code: 'recipe_blocked' }
  | { code: 'component_cost_missing'; componentKeys: string[] }
  | { code: 'cycle' };

/** A product (table `product`). */
export interface PlannedProduct {
  key: string;
  /** The source record decisions about this product are made on (`set_cost`, …). */
  primaryRef: string;
  /** The item whose category and flags classified it. */
  anchorItemRef: string;
  name: string;
  roles: ProductRole[];
  /** For a `service` product: what it is, guessed from its name (B-031); else null. */
  serviceKind: ServiceKind | null;
  baseUom: string;
  tracksExpiry: boolean;
  deductionMode: DeductionMode;
  categoryKey: string | null;
  familyKey: string | null;
  familyRole: FamilyRole | null;
  stateRuleKey: string | null;
  channelVeto: ChannelVeto;
  /** Standard cost per base unit, whole cents; `null` when not computable. */
  standardCostCents: Cents | null;
  costMethod: CostMethod | null;
  costGap: CostGap | null;
  /** Every source item and variant that became (part of) this product. */
  sourceRefs: string[];
}

/** Where an identifier's value came from. */
export type IdentifierOrigin = 'source' | 'resourced' | 'manual';

/** An identifier (table `product_identifier`). */
export interface PlannedIdentifier {
  key: string;
  productKey: string;
  kind: IdentifierKind;
  value: string;
  canonicalValue: string;
  /** The source record it came from. */
  sourceRef: string;
  origin: IdentifierOrigin;
  /** For a re-sourced GTIN: which exports supplied it. */
  resourcedFrom: string | null;
  /** SKU look-alike warnings. */
  warnings: SkuIssue[];
}

/** Why a recipe component could not be resolved to a product. */
export type UnresolvedReason =
  'missing' | 'ignored' | 'not_classified' | 'variant_required' | 'non_positive_qty';

/** A recipe line (table `recipe_line`). */
export interface PlannedRecipeLine {
  key: string;
  position: number;
  /** `null` when unresolved. */
  componentKey: string | null;
  /** The source component (`item:…`, plus `variant:…` when given). */
  componentSourceRef: string;
  componentVariantRef: string | null;
  /** Full precision, in `uom`. */
  qty: string;
  /** The component's base unit; `null` when unresolved. */
  uom: string | null;
  lossPct: string;
  allowedStates: string[];
  warnings: PrecisionWarning[];
  unresolved: UnresolvedReason | null;
}

/** A recipe with its draft version 1 (tables `recipe`, `recipe_version`, `recipe_line`). */
export interface PlannedRecipe {
  key: string;
  productKey: string;
  /** The source item the components came from (a recipe twin or the composite itself). */
  sourceItemRef: string;
  origin: 'recipe_twin' | 'composite';
  /** An unresolved line blocks loading this recipe until the review item is decided. */
  blocked: boolean;
  /** All measured quantities look rounded (see rules `recipes.roundedQuantityStep`). */
  roundedQuantities: boolean;
  /** Components left out by a `drop_component` decision. */
  droppedComponentRefs: string[];
  version: {
    key: string;
    versionNo: 1;
    status: 'draft';
    batchYieldQty: string;
    yieldUom: string;
    lines: PlannedRecipeLine[];
  };
}

/** The state rule shared by every expiry-state group (table `state_rule`). */
export interface PlannedStateRule {
  key: string;
  name: string;
  appliesTo: 'product';
  states: StateRuleState[];
}

/** A channel mapping for the import's source channel (table `channel_mapping`). */
export interface PlannedChannelMapping {
  key: string;
  sourceRef: string;
  productKey: string;
  state: string | null;
  externalItemId: string;
  externalVariantId: string;
  matchMethod: 'identifier' | 'manual';
  /** Key of the identifier that made the match (`identifier` method only). */
  matchedByIdentifierKey: string | null;
  status: 'active' | 'vetoed';
  statusReason: string | null;
  /**
   * Source stores where the variant is not for sale: the loader makes no mapping on those
   * stores' channels (absent in plans made before this field existed = none).
   */
  unavailableStoreRefs: string[];
}

/** A unit conversion (table `uom_conversion`). */
export interface PlannedUomConversion {
  key: string;
  productKey: string | null;
  from: string;
  to: string;
  factor: string;
}

/** What happened to a source barcode. */
export interface BarcodeOutcome {
  sourceRef: string;
  value: string;
  outcome: 'imported' | 'resourced' | 'manual' | 'flagged' | 'not_imported';
  reason: string | null;
}

/** Why a source record could not be classified. */
export type CouldNotClassifyReason =
  | 'category_role_unknown'
  | 'twin_ambiguous'
  | 'twin_unpaired'
  | 'partner_not_classified'
  | 'state_group_uncertain';

/** The outcome of one source record: exactly one per item and per variant. */
export type RecordOutcome =
  | { kind: 'mapped'; targetKey: string }
  | { kind: 'merged_into_family'; familyKey: string; productKey: string }
  | { kind: 'state_mapping'; productKey: string; state: string }
  | { kind: 'ignored'; reason: string }
  | { kind: 'could_not_classify'; reason: CouldNotClassifyReason };

/** Kinds of {@link RecordOutcome}. */
export const OUTCOME_KINDS = [
  'mapped',
  'merged_into_family',
  'state_mapping',
  'ignored',
  'could_not_classify',
] as const;
/** One of {@link OUTCOME_KINDS}. */
export type OutcomeKind = (typeof OUTCOME_KINDS)[number];

/** A source record and its outcome. */
export interface SourceRecordOutcome {
  sourceRef: string;
  recordKind: 'item' | 'variant';
  /** The item (itself, or the variant's item). */
  itemRef: string;
  outcome: RecordOutcome;
}

/** Kinds of review item. */
export const REVIEW_KINDS = [
  'sku_collision',
  'sku_invalid',
  'twin_ambiguous',
  'twin_unpaired',
  'state_group_uncertain',
  'category_role_unknown',
  'barcode_corrupt_unresolved',
  'gtin_collision',
  'rounded_quantity',
  'component_unresolved',
  'uom_missing',
  'cost_not_computable',
] as const;
/** One of {@link REVIEW_KINDS}. */
export type ReviewKind = (typeof REVIEW_KINDS)[number];

/** Something a person must decide. Deciding it (a {@link ManualDecision}) resolves it. */
export interface ReviewItem {
  /** `<kind>:<primary source ref>`; stable across runs. */
  key: string;
  kind: ReviewKind;
  sourceRefs: string[];
  message: string;
  /** Decisions that would resolve it (a starting point, applied together); may be empty. */
  suggestedDecisions: ManualDecision[];
}

/** A stored rule that did not apply to this catalogue (e.g. a decision about a record that is gone). */
export interface UnusedRule {
  sourceRef: string;
  reason: string;
}

/** Everything {@link planImport} produces. */
export interface ImportPlan {
  format: typeof IMPORT_PLAN_FORMAT;
  rulesRevision: number;
  source: { connectorKind: string; targetIdentity: string; extractedAt: string };
  categories: PlannedCategory[];
  families: PlannedFamily[];
  products: PlannedProduct[];
  identifiers: PlannedIdentifier[];
  recipes: PlannedRecipe[];
  stateRules: PlannedStateRule[];
  channelMappings: PlannedChannelMapping[];
  uomConversions: PlannedUomConversion[];
  barcodes: BarcodeOutcome[];
  outcomes: SourceRecordOutcome[];
  reviewItems: ReviewItem[];
  unusedRules: UnusedRule[];
}
